import Button from '@passkeys/ui/button';
import Text from '@passkeys/ui/text';
import TokenIcon from '@passkeys/ui/token-icon';

import { actions } from '@/ui/flux';
import useAsset from '@/ui/hooks/use-asset';
import useNavigate from '@/ui/hooks/use-navigate';
import type { ReferrerLocationState } from '@/ui/utils/navigation';
import { createNavigateStateOptions } from '@/ui/utils/navigation';

export type ReceiveFailModalAction = {
  label: string;
  route: string;
};

type ShowReceiveFailModalOptions = {
  assetName: string;
  type: 'swap' | 'buy';
  primaryAction: ReceiveFailModalAction;
  onClose?: () => void;
};

const ReceiveFailModal = ({
  assetName,
  type,
  primaryAction,
  onClose,
}: ShowReceiveFailModalOptions) => {
  const navigate = useNavigate();
  const asset = useAsset(assetName);
  const isSwap = type === 'swap';

  if (!asset) {
    throw new Error(`Invalid asset ${assetName}`);
  }

  const navigateAndClose = (route: string) => {
    navigate(
      route,
      createNavigateStateOptions<ReferrerLocationState>({
        referrer: 'receive-fail',
      }),
    );

    onClose?.();
  };

  return (
    <div className="flex h-full flex-col pb-8">
      <div className="flex w-full flex-1 flex-col items-center justify-center">
        <TokenIcon
          name={asset.name}
          displayName={asset.displayName}
          size={80}
        />

        <div className="mt-8 flex w-full flex-col items-center gap-y-2">
          <Text size={24} align="center" className="leading-none">
            Failed to receive {asset.displayName}
          </Text>
        </div>
        <Text
          align="center"
          size={14}
          className="mt-10 max-w-[264px] text-gray-600 theme-dark:text-neutral-400"
        >
          Your funds didn't leave your wallet. Please try to make a new{' '}
          {isSwap ? 'swap' : 'purchase'}.
        </Text>
      </div>
      <div className="flex w-full flex-col items-center">
        <Button
          className="w-full max-w-[296px]"
          size="large"
          onClick={() => navigateAndClose(primaryAction.route)}
          dataTestId="failure-primary-button"
        >
          {primaryAction.label}
        </Button>
        <Button
          className="-mb-2 mt-2"
          onClick={() => navigateAndClose('/')}
          variant="tertiary"
          dataTestId="failure-tertiary-button"
        >
          Back to home
        </Button>
      </div>
    </div>
  );
};

const showReceiveFailModal = async ({
  assetName,
  type,
  primaryAction,
  onClose,
}: ShowReceiveFailModalOptions) =>
  actions.modal.show({
    canClose: false,
    fullscreen: true,
    title: type === 'swap' ? 'Swapping' : 'Buying',
    onClose,
    reactContent: (
      <ReceiveFailModal
        assetName={assetName}
        type={type}
        primaryAction={primaryAction}
      />
    ),
  });

export default showReceiveFailModal;
